/**
 * Markdown 本文からメディア（画像 / 動画 / 音声）参照を取り出すユーティリティ。
 *
 * 共有ノートのカード（共有TIPS）は、サムネイルを個別アップロードせず
 * 「本文の一番最初に添付されたメディア」を自動採用する。本文には
 * `![alt](url)`（画像）と `[📎 名前](url)`（その他の添付）が混在するため、
 * 両記法を出現順に走査して最初のメディアを拾う。
 *
 * リンク記法の正規表現（MD_LINK_SOURCE）はここを一次定義とし、ノートの
 * zip エクスポート（pages/notes/utils/export.ts）からも参照される。
 */

export type MarkdownMediaKind = "image" | "video" | "audio";

export interface MarkdownMedia {
  kind: MarkdownMediaKind;
  url: string;
}

export interface LeadMediaResult {
  /** 先頭のメディア（本文にメディアが無ければ null） */
  media: MarkdownMedia | null;
  /**
   * 採用したメディアの記法を取り除いた本文。サムネイルと本文で同じ
   * メディアが二重に表示されるのを防ぐ（未採用時は原文のまま）。
   */
  content: string;
}

/**
 * md のリンク構文（`![alt](url)` / `[label](url)`）にマッチする正規表現の
 * ソース。group 1 = `!`（画像記法のとき）、group 2 = URL。
 * ラベル内のエスケープ（`\[` / `\]`）にも対応する。
 */
export const MD_LINK_SOURCE = String.raw`(!?)\[(?:[^\]\\]|\\.)*\]\((https?:\/\/[^)\s]+)\)`;

/** ブラウザで cover 表示できる画像拡張子。 */
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"];
/** `<video>` で先頭フレームを描画できる動画拡張子（m3u8 は hls.js が要るため除外）。 */
const VIDEO_EXTENSIONS = ["mp4", "webm", "mov", "m4v"];
/** 生成サムネイル（GenerativeAudioThumbnail）で描くオーディオ拡張子。 */
const AUDIO_EXTENSIONS = ["mp3", "wav", "aac", "m4a", "ogg", "flac"];

const extOfUrl = (url: string): string => {
  try {
    return new URL(url).pathname.split(".").pop()?.toLowerCase() ?? "";
  } catch {
    return url.split(/[?#]/)[0].split(".").pop()?.toLowerCase() ?? "";
  }
};

/** URL の拡張子からメディア種別を判定する（対象外は null）。 */
export const mediaKindOfUrl = (url: string): MarkdownMediaKind | null => {
  const ext = extOfUrl(url);
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (VIDEO_EXTENSIONS.includes(ext)) return "video";
  if (AUDIO_EXTENSIONS.includes(ext)) return "audio";
  return null;
};

/** メディア記法を取り除いた後に残る余分な空行を整える。 */
const tidyContent = (text: string): string =>
  text.replace(/\n{3,}/g, "\n\n").replace(/^[ \t]*\n+/, "");

/**
 * 本文の先頭メディアを 1 件抽出し、その記法を除去した本文とともに返す。
 *
 * - 画像記法（`![...]`）は拡張子が不明でも画像として採用する
 *   （エディタは画像を必ずこの記法で挿入するため）
 * - 通常リンクは拡張子が動画 / 音声のときだけメディア扱いし、
 *   PDF などの添付はスキップして次の候補を探す
 */
export const extractLeadMedia = (content: string): LeadMediaResult => {
  const re = new RegExp(MD_LINK_SOURCE, "g");
  let match: RegExpExecArray | null;
  while ((match = re.exec(content)) !== null) {
    const isImageSyntax = match[1] === "!";
    const url = match[2];
    const kind = mediaKindOfUrl(url) ?? (isImageSyntax ? "image" : null);
    if (!kind) continue;
    const removed =
      content.slice(0, match.index) +
      content.slice(match.index + match[0].length);
    return { media: { kind, url }, content: tidyContent(removed) };
  }
  return { media: null, content };
};
